import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Plus, ShieldCheck } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";

import { ApiError, type CustomRole, type Role, customRolesApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { toast } from "../lib/toast";

export const Route = createFileRoute("/_app/settings/roles")({
  beforeLoad: () => {
    const user = useAuthStore.getState().user;
    if (user && user.role !== "admin") throw redirect({ to: "/settings/tags" });
  },
  component: CustomRolesPage,
});

type BaseRole = Exclude<Role, "admin">;

function CustomRolesPage() {
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ["custom-roles"], queryFn: customRolesApi.list });
  const catalogueQuery = useQuery({
    queryKey: ["custom-role-permissions"],
    queryFn: customRolesApi.permissions,
  });
  const [selectedId, setSelectedId] = useState<string>("new");
  const selected = rolesQuery.data?.items.find((role) => role.id === selectedId) ?? null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["custom-roles"] });

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <ShieldCheck className="size-5 opacity-70" aria-hidden />
            Custom roles
          </h2>
          <p className="text-sm opacity-60">
            Create reusable, least-privilege access profiles for non-Admin employees.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSelectedId("new")}
          className="inline-flex min-h-11 items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white"
        >
          <Plus className="size-4" aria-hidden />
          New role
        </button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="space-y-2" aria-label="Custom roles">
          {rolesQuery.data?.items.map((role) => (
            <button
              key={role.id}
              type="button"
              onClick={() => setSelectedId(role.id)}
              className={`min-h-11 w-full rounded-lg border p-3 text-left ${
                selectedId === role.id
                  ? "border-[var(--accent)] bg-[var(--hover)]"
                  : "border-[var(--border)]"
              }`}
            >
              <span className="block font-medium">{role.name}</span>
              <span className="block text-xs opacity-60">
                {role.base_role} base · {role.assigned_users} assigned
                {!role.is_active ? " · inactive" : ""}
              </span>
            </button>
          ))}
          {!rolesQuery.isLoading && !rolesQuery.data?.items.length && (
            <p className="rounded-lg border border-dashed border-[var(--border)] p-4 text-sm opacity-60">
              No custom roles yet.
            </p>
          )}
        </aside>

        {catalogueQuery.data && (
          <RoleEditor
            key={selected?.id ?? "new"}
            role={selected}
            catalogue={catalogueQuery.data}
            onSaved={(role) => {
              refresh();
              setSelectedId(role.id);
            }}
          />
        )}
      </div>
    </div>
  );
}

function RoleEditor({
  role,
  catalogue,
  onSaved,
}: {
  role: CustomRole | null;
  catalogue: Awaited<ReturnType<typeof customRolesApi.permissions>>;
  onSaved: (role: CustomRole) => void;
}) {
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [baseRole, setBaseRole] = useState<BaseRole>(role?.base_role ?? "manager");
  const [permissions, setPermissions] = useState<string[]>(role?.permissions ?? []);
  const [isActive, setIsActive] = useState(role?.is_active ?? true);

  const allowed = useMemo(
    () => new Set(catalogue.allowed_by_base_role[baseRole] ?? []),
    [catalogue, baseRole],
  );
  const grouped = useMemo(() => {
    const groups = new Map<string, typeof catalogue.items>();
    for (const item of catalogue.items.filter((option) => allowed.has(option.code))) {
      const group = (item.code.split(".")[0] ?? "other").replaceAll("_", " ");
      groups.set(group, [...(groups.get(group) ?? []), item]);
    }
    return [...groups.entries()];
  }, [allowed, catalogue.items]);

  useEffect(() => {
    setPermissions((current) => current.filter((permission) => allowed.has(permission)));
  }, [allowed]);

  const mutation = useMutation({
    mutationFn: () =>
      role
        ? customRolesApi.update(role.id, {
            name,
            description: description || null,
            base_role: baseRole,
            permissions,
            is_active: isActive,
          })
        : customRolesApi.create({
            name,
            description: description || null,
            base_role: baseRole,
            permissions,
          }),
    onSuccess: (saved) => {
      toast.success(role ? "Custom role updated." : "Custom role created.");
      onSaved(saved);
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiError
          ? error.reauthRequired
            ? "Sign out and sign in again before changing access."
            : error.message
          : "Could not save the custom role.",
      ),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-5 rounded-xl border border-[var(--border)] p-4 sm:p-5"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block">Role name</span>
          <input
            className="form-input w-full"
            required
            minLength={2}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block">Visibility model</span>
          <select
            className="form-input w-full"
            value={baseRole}
            onChange={(event) => setBaseRole(event.target.value as BaseRole)}
          >
            <option value="manager">Manager</option>
            <option value="sales">Sales</option>
            <option value="production">Production-safe</option>
          </select>
        </label>
      </div>
      <label className="text-sm">
        <span className="mb-1 block">Description</span>
        <textarea
          className="form-input min-h-20 w-full"
          maxLength={500}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </label>

      <div className="space-y-4">
        <div>
          <h3 className="font-medium">Allowed work</h3>
          <p className="text-xs opacity-60">Anything left unchecked is denied by the server.</p>
        </div>
        {grouped.map(([group, options]) => (
          <fieldset key={group} className="rounded-lg border border-[var(--border)] p-3">
            <legend className="px-1 text-sm font-medium capitalize">{group}</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {options.map((option) => (
                <label key={option.code} className="flex min-h-11 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={permissions.includes(option.code)}
                    onChange={(event) =>
                      setPermissions((current) =>
                        event.target.checked
                          ? [...current, option.code]
                          : current.filter((permission) => permission !== option.code),
                      )
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      {role && (
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Role is active
        </label>
      )}
      <p className="rounded-md bg-[var(--hover)] p-3 text-xs opacity-75">
        Admin/Owner controls, imports, deletion, Trash, sessions, and integrations cannot be
        delegated.
      </p>
      <button
        type="submit"
        disabled={mutation.isPending || !name.trim()}
        className="min-h-11 rounded-md bg-[var(--accent)] px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
      >
        {role ? "Save role" : "Create role"}
      </button>
    </form>
  );
}
